import type { MonitorRow } from './types';
import { diffDays } from './format';

/**
 * Contractor Monitor colour rules (per Jason, 2026-10-12):
 *
 *   Days since last contact — green ≤ 10, amber ≤ 30, red > 30;
 *     never contacted is red.
 *   Likely to extend — red when still "Not discussed" and the contract
 *     ends within 30 days.
 *   Mobile — red when neither personal nor work mobile is recorded.
 */
export type Rag = 'green' | 'amber' | 'red';

export const CONTACT_GREEN_MAX_DAYS = 10;
export const CONTACT_AMBER_MAX_DAYS = 30;
export const NOT_DISCUSSED_WINDOW_DAYS = 30;

export function daysSinceContact(row: MonitorRow, today: string): number | null {
  return row.lastContacted ? diffDays(row.lastContacted, today) : null;
}

export function contactRag(days: number | null): Rag {
  if (days === null) return 'red';
  if (days <= CONTACT_GREEN_MAX_DAYS) return 'green';
  if (days <= CONTACT_AMBER_MAX_DAYS) return 'amber';
  return 'red';
}

export function daysToEnd(row: MonitorRow, today: string): number {
  return diffDays(today, row.endDate);
}

export function likelyIsRed(row: MonitorRow, today: string): boolean {
  return row.likelyToExtend === 'not-discussed' && daysToEnd(row, today) <= NOT_DISCUSSED_WINDOW_DAYS;
}

export function mobileIsMissing(row: MonitorRow): boolean {
  return !row.mobile;
}

/**
 * The row's overall status — does this contractor need a call? Red when the
 * contact is over 30 days / never, or extension is "Not discussed" with the
 * contract ending within 30 days; amber at 11–30 days; green otherwise.
 * A missing mobile is flagged in its own cell, not here: it's a data fix,
 * and colouring the row would make a contractor called yesterday look urgent.
 */
export function rowRag(row: MonitorRow, today: string): Rag {
  const contact = contactRag(daysSinceContact(row, today));
  if (contact === 'red' || likelyIsRed(row, today)) return 'red';
  return contact;
}

/** Urgency for the default sort: never contacted first, then longest gap. */
export function urgencyScore(row: MonitorRow, today: string): number {
  const days = daysSinceContact(row, today);
  return days === null ? Number.MAX_SAFE_INTEGER : days;
}
